import React, { useMemo, useState } from "react";
import {
  MdDashboard,
  MdConfirmationNumber,
  MdSchedule,
  MdCategory,
  MdDirectionsBus,
  MdPolicy,
  MdLogout,
  MdSearch,
  MdNotificationsNone,
  MdHelpOutline,
  MdLocalOffer,
  MdCampaign,
  MdStarOutline,
  MdSupportAgent,
  MdPrivacyTip,
  MdPerson,
  MdBusiness,
  MdContactPhone,
  MdReceiptLong,
  MdAccountBalance,
  MdSettings,
  MdKeyboardArrowRight,
} from "react-icons/md";

import BusDashboard from "./BusDashboard";
import BusBooking from "./BusBooking";
import BusSchedule from "./BusSchedule";
import BusTypes from "./BusTypes";
import BusFleet from "./BusFleet";
import BusCancellationPolicy from "./BusCancellationPolicy";


import VendorOffers from "../Place/VendorOffers";
import VendorAdvertisements from "../Place/VendorAdvertisements";
import VendorSupport from "../Place/VendorSupport";
import VendorPolicies from "../Place/VendorPolicies";
import VendorRatingsReviews from "../VendorRatingsReviews";
import VendorProfile from "../VendorProfile";

import "./BusVendorLayout.css";

/* ===================================================
   SIDEBAR MENU
   =================================================== */

const MENU = [
  { id: "dashboard", label: "Dashboard", icon: <MdDashboard />, section: "OPERATIONS" },
  { id: "bus-booking", label: "Bus Booking", icon: <MdConfirmationNumber />, section: "OPERATIONS" },
  { id: "bus-schedule", label: "Bus Schedule", icon: <MdSchedule />, section: "OPERATIONS" },

  { id: "bus-types", label: "Bus Types", icon: <MdCategory />, section: "MANAGEMENT" },
  { id: "buses", label: "Buses", icon: <MdDirectionsBus />, section: "MANAGEMENT" },
  { id: "cancellation-policy", label: "Cancellation Policy", icon: <MdPolicy />, section: "MANAGEMENT" },

  { id: "vendor-offers", label: "Offers & Promotions", icon: <MdLocalOffer />, section: "GROW & SUPPORT" },
  { id: "vendor-ads", label: "Advertisements", icon: <MdCampaign />, section: "GROW & SUPPORT" },
  { id: "vendor-reviews", label: "Ratings & Reviews", icon: <MdStarOutline />, section: "GROW & SUPPORT" },
  { id: "vendor-support", label: "Support", icon: <MdSupportAgent />, section: "GROW & SUPPORT" },
  { id: "vendor-policies", label: "Privacy & Policies", icon: <MdPrivacyTip />, section: "GROW & SUPPORT" },
];

/* ===================================================
   PROFILE MENU (ACCOUNT section, collapsible)
   id  ->  VendorProfile ke initialSection value
   =================================================== */

const PROFILE_MENU = [
  { id: "profile-personal", label: "Personal Details", icon: <MdPerson />, section: "personal" },
  { id: "profile-business", label: "Business Details", icon: <MdBusiness />, section: "business" },
  { id: "profile-contact", label: "Contact Details", icon: <MdContactPhone />, section: "contact" },
  { id: "profile-tax", label: "Tax & Identity", icon: <MdReceiptLong />, section: "tax" },
  { id: "profile-bank", label: "Bank Details", icon: <MdAccountBalance />, section: "bank" },
  { id: "profile-service", label: "Service Details", icon: <MdSettings />, section: "service" },
];

const PROFILE_SECTION_BY_ID = PROFILE_MENU.reduce((acc, item) => {
  acc[item.id] = item.section;
  return acc;
}, {});

/* ===================================================
   VENDOR HELPERS
   =================================================== */

function getVendorUser() {
  try {
    return JSON.parse(localStorage.getItem("vendorUser") || "{}");
  } catch {
    return {};
  }
}

function logout() {
  if (window.confirm("Are you sure you want to sign out?")) {
    localStorage.removeItem("vendorToken");
    localStorage.removeItem("vendorUser");
    window.location.href = "/vendor-login";
  }
}

/* ===================================================
   LAYOUT COMPONENT
   =================================================== */

export default function BusVendorLayout() {
  const [active, setActive] = useState("dashboard");
  const [search, setSearch] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);

  const vendor = useMemo(() => getVendorUser(), []);
  const vendorName = vendor?.name || vendor?.companyName || "Bus Operator";
  const vendorInitials = vendorName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const isProfileActive = active.startsWith("profile-");
  const isSearching = search.trim().length > 0;

  /* ── Filter menu by search ── */
  const filteredMenu = useMemo(() => {
    if (!search.trim()) return MENU;
    const q = search.toLowerCase();
    return MENU.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.id.toLowerCase().includes(q)
    );
  }, [search]);

  const filteredProfileMenu = useMemo(() => {
    if (!search.trim()) return PROFILE_MENU;
    const q = search.toLowerCase();
    return PROFILE_MENU.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.id.toLowerCase().includes(q) ||
        "my profile account".includes(q)
    );
  }, [search]);

  /* ── Group by section ── */
  const groupedMenu = useMemo(() => {
    const groups = {};
    filteredMenu.forEach((item) => {
      const section = item.section || "GENERAL";
      if (!groups[section]) groups[section] = [];
      groups[section].push(item);
    });
    return groups;
  }, [filteredMenu]);

  /* Profile submenu: khula rahe agar toggle on ho, koi profile page active ho, ya search chal rahi ho */
  const showProfileItems = profileOpen || isProfileActive || isSearching;

  const openProfile = (id = "profile-personal") => {
    setActive(id);
    setProfileOpen(true);
  };

  /* ── Render active content ── */
  const content = useMemo(() => {
    if (PROFILE_SECTION_BY_ID[active]) {
      // key lagaya hai taaki section badalne par VendorProfile fresh mount ho
      return <VendorProfile key={active} initialSection={PROFILE_SECTION_BY_ID[active]} />;
    }

    switch (active) {
      case "dashboard":
        return <BusDashboard onNavigate={setActive} />;

      case "bus-booking":
      case "bookings":
      case "other-bookings":
        return <BusBooking onNavigate={setActive} />;

      case "bus-schedule":
      case "schedule":
      case "trips":
      case "services":
        return <BusSchedule onNavigate={setActive} />;

      case "bus-types":
        return <BusTypes onNavigate={setActive} />;

      case "buses":
      case "fleet":
        return <BusFleet onNavigate={setActive} />;

      case "cancellation-policy":
      case "policy":
      case "cancellation":
        return <BusCancellationPolicy onNavigate={setActive} />;

      /* ---------- GROW & SUPPORT ---------- */
      case "vendor-offers":
        return <VendorOffers />;

      case "vendor-ads":
        return <VendorAdvertisements />;

      case "vendor-reviews":
        return <VendorRatingsReviews />;

      case "vendor-support":
        return <VendorSupport />;

      case "vendor-policies":
        return <VendorPolicies />;

      default:
        return <BusDashboard onNavigate={setActive} />;
    }
  }, [active]);

  return (
    <div className="bvl">
      {/* ===== SIDEBAR ===== */}
      <aside className="bvl-sidebar">
        {/* Brand */}
        <div className="bvl-brand">
          <div className="bvl-brand-icon">BMT</div>
          <div className="bvl-brand-text">
            <b>BMT Connect</b>
            <small>Bus Operator Panel</small>
          </div>
        </div>

        {/* Search */}
        <div className="bvl-search">
          <MdSearch className="bvl-search-icon" />
          <input
            placeholder="Search menu…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Navigation */}
        <nav className="bvl-nav">
          {Object.entries(groupedMenu).map(([section, items]) => (
            <React.Fragment key={section}>
              <span className="bvl-nav-label">{section}</span>
              {items.map((item) => (
                <button
                  key={item.id}
                  className={`bvl-nav-btn${active === item.id ? " active" : ""}`}
                  onClick={() => setActive(item.id)}
                >
                  <span className="bvl-nav-icon">{item.icon}</span>
                  <span className="bvl-nav-text">{item.label}</span>
                </button>
              ))}
            </React.Fragment>
          ))}

          {/* ACCOUNT / MY PROFILE */}
          {filteredProfileMenu.length > 0 && (
            <React.Fragment>
              <span className="bvl-nav-label">ACCOUNT</span>

              <button
                type="button"
                className={`bvl-nav-btn${isProfileActive ? " active" : ""}`}
                onClick={() => setProfileOpen((prev) => !prev)}
                aria-expanded={showProfileItems}
              >
                <span className="bvl-nav-icon">
                  <MdPerson />
                </span>
                <span className="bvl-nav-text">My Profile</span>
                <span
                  style={{
                    marginLeft: "auto",
                    display: "inline-flex",
                    fontSize: 20,
                    transition: "transform .2s",
                    transform: showProfileItems ? "rotate(90deg)" : "none",
                  }}
                >
                  <MdKeyboardArrowRight />
                </span>
              </button>

              {showProfileItems &&
                filteredProfileMenu.map((item) => (
                  <button
                    key={item.id}
                    className={`bvl-nav-btn${active === item.id ? " active" : ""}`}
                    style={{ paddingLeft: 34 }}
                    onClick={() => setActive(item.id)}
                  >
                    <span className="bvl-nav-icon">{item.icon}</span>
                    <span className="bvl-nav-text">{item.label}</span>
                  </button>
                ))}
            </React.Fragment>
          )}
        </nav>

        {/* Logout */}
        <div className="bvl-logout">
          <button className="bvl-logout-btn" onClick={logout}>
            <span className="bvl-nav-icon">
              <MdLogout />
            </span>
            <span className="bvl-nav-text">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* ===== CONTENT SHELL ===== */}
      <section className="bvl-content">
        {/* Header */}
        <header className="bvl-header">
          <div className="bvl-header-left">
            <b>Bus Management Panel</b>
            <small>BirdMyTrip Partner Operations</small>
          </div>

          <div className="bvl-header-right">
            {/* RedBus-style TIN/Mobile search */}
            <div className="bvl-header-quick-search">
              <input
                type="text"
                placeholder="Enter TIN or Mobile"
                className="bvl-header-quick-input"
              />
              <button className="bvl-header-quick-btn" title="Search Ticket">
                <MdSearch />
              </button>
            </div>

            <button
              className="bvl-header-icon-btn"
              title="Help"
              onClick={() => setActive("vendor-support")}
            >
              <MdHelpOutline />
            </button>

            <button className="bvl-header-icon-btn bvl-has-badge" title="Notifications">
              <MdNotificationsNone />
              <span className="bvl-badge">1</span>
            </button>

            {/* Header user: click karne par My Profile khulta hai */}
            <div
              className="bvl-header-user"
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              title="My Profile"
              onClick={() => openProfile("profile-personal")}
              onKeyDown={(e) => e.key === "Enter" && openProfile("profile-personal")}
            >
              <div className="bvl-header-avatar">{vendorInitials}</div>
              <div className="bvl-header-user-info">
                <b>{vendorName}</b>
                <small>Approved Vendor</small>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="bvl-main">{content}</main>
      </section>
    </div>
  );
}